import React from "react";
import {
  Factory,
  Package,
  ShieldCheck,
  Target,
  Compass,
  ChevronRight,
  ChevronLeft,
  Download,
} from "lucide-react";

// ----- Vision & Mission -----
const PILLAR_IMAGES = {
  design:
    "https://customer-assets-jai6qajn.emergentagent.net/job_carousel-preview-9/artifacts/49oup9s3_image.png",
  manufacturing:
    "https://customer-assets-jai6qajn.emergentagent.net/job_carousel-preview-9/artifacts/02ckx4vq_image.png",
  craftsmanship:
    "https://images.unsplash.com/photo-1617694820985-a5476fe22722?crop=entropy&cs=srgb&fm=jpg&q=80&w=1200",
  delivery:
    "https://customer-assets-jai6qajn.emergentagent.net/job_carousel-preview-9/artifacts/ro5g3v2a_image.png",
};

// Reveal-on-scroll wrapper using IntersectionObserver
const Reveal = ({ children, delay = 0, className = "" }) => {
  const ref = React.useRef(null);
  const [visible, setVisible] = React.useState(false);
  React.useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisible(true);
            obs.disconnect();
          }
        });
      },
      { threshold: 0.15 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return (
    <div
      ref={ref}
      className={className}
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? "translateY(0)" : "translateY(28px)",
        transition: `opacity 800ms ease ${delay}ms, transform 800ms cubic-bezier(0.22, 1, 0.36, 1) ${delay}ms`,
      }}
    >
      {children}
    </div>
  );
};

// Animated count-up number
const CountUp = ({ end, duration = 1600, suffix = "" }) => {
  const ref = React.useRef(null);
  const [value, setValue] = React.useState(0);
  const [started, setStarted] = React.useState(false);
  React.useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !started) {
            setStarted(true);
            const startTime = performance.now();
            const step = (t) => {
              const p = Math.min(1, (t - startTime) / duration);
              const eased = 1 - Math.pow(1 - p, 3);
              setValue(Math.round(end * eased));
              if (p < 1) requestAnimationFrame(step);
            };
            requestAnimationFrame(step);
          }
        });
      },
      { threshold: 0.4 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [end, duration, started]);
  return (
    <span ref={ref}>
      {value.toLocaleString()}
      {suffix}
    </span>
  );
};

// 3D tilt vision/mission statement card
const StatementCard = ({ icon: Icon, iconBg, iconColor, tag, title, body, delay }) => {
  const cardRef = React.useRef(null);
  const [tilt, setTilt] = React.useState({ x: 0, y: 0, gx: 50, gy: 50, hover: false });
  const onMove = (e) => {
    const el = cardRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const px = (e.clientX - r.left) / r.width;
    const py = (e.clientY - r.top) / r.height;
    setTilt({ x: (0.5 - py) * 8, y: (px - 0.5) * 8, gx: px * 100, gy: py * 100, hover: true });
  };
  const onLeave = () => setTilt({ x: 0, y: 0, gx: 50, gy: 50, hover: false });

  return (
    <Reveal delay={delay}>
      <div
        className="relative h-full"
        style={{ perspective: "1200px" }}
        onMouseMove={onMove}
        onMouseLeave={onLeave}
      >
        <div
          ref={cardRef}
          className="relative rounded-2xl p-8 md:p-10 h-full overflow-hidden"
          style={{
            backgroundColor: "#ffffff",
            border: "1px solid #F2D9B8",
            transformStyle: "preserve-3d",
            transform: `rotateX(${tilt.x}deg) rotateY(${tilt.y}deg) translateZ(${tilt.hover ? 12 : 0}px)`,
            transition: tilt.hover
              ? "transform 80ms linear, box-shadow 300ms ease"
              : "transform 700ms cubic-bezier(0.22, 1, 0.36, 1), box-shadow 300ms ease",
            boxShadow: tilt.hover
              ? "0 40px 80px -30px rgba(15,28,58,0.3), 0 20px 40px -20px rgba(178,38,38,0.15)"
              : "0 15px 35px -18px rgba(15,28,58,0.15)",
          }}
        >
          <div
            className="w-14 h-14 rounded-full flex items-center justify-center mb-5"
            style={{ backgroundColor: iconBg, transform: "translateZ(30px)" }}
          >
            <Icon className="w-7 h-7" style={{ color: iconColor }} strokeWidth={2} />
          </div>
          <div
            className="text-xs font-semibold tracking-widest uppercase mb-2"
            style={{ color: "#B22626", transform: "translateZ(20px)" }}
          >
            {tag}
          </div>
          <h3
            className="text-[#0F1C3A] mb-4 leading-tight"
            style={{
              fontFamily: "'Playfair Display', Georgia, serif",
              fontSize: "clamp(1.6rem, 2.4vw, 2rem)",
              fontWeight: 600,
              transform: "translateZ(25px)",
            }}
          >
            {title}
          </h3>
          <p
            className="text-slate-700 leading-relaxed text-[15px]"
            style={{ transform: "translateZ(15px)" }}
          >
            {body}
          </p>
          {/* Cursor glare */}
          <div
            className="pointer-events-none absolute inset-0 rounded-2xl transition-opacity duration-300"
            style={{
              opacity: tilt.hover ? 1 : 0,
              background: `radial-gradient(circle at ${tilt.gx}% ${tilt.gy}%, rgba(242,140,31,0.18) 0%, rgba(242,140,31,0) 45%)`,
            }}
          />
          {/* Corner accent that grows on hover */}
          <div
            className="pointer-events-none absolute top-0 right-0 w-24 h-24 rounded-bl-full transition-all duration-500"
            style={{
              background: "linear-gradient(135deg, rgba(242,140,31,0.15) 0%, rgba(178,38,38,0.15) 100%)",
              opacity: tilt.hover ? 1 : 0.4,
              transform: tilt.hover ? "scale(1.15)" : "scale(1)",
            }}
          />
        </div>
      </div>
    </Reveal>
  );
};

// Pillar card with image, hover overlay reveal and animated icon
const PillarCard = ({ img, icon: Icon, title, desc, stat, statLabel, delay }) => {
  const [hover, setHover] = React.useState(false);
  const imgRef = React.useRef(null);
  const rafRef = React.useRef(0);
  const hoverRef = React.useRef(false);
  const startRef = React.useRef(performance.now() + Math.random() * 3000); // random phase offset per card
  const reducedRef = React.useRef(false);

  React.useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    reducedRef.current = mq.matches;
    const update = () => (reducedRef.current = mq.matches);
    mq.addEventListener?.("change", update);
    return () => mq.removeEventListener?.("change", update);
  }, []);

  React.useEffect(() => {
    if (reducedRef.current) return;
    const loop = () => {
      const el = imgRef.current;
      if (el) {
        const t = (performance.now() - startRef.current) / 1000;
        // gentle continuous drift & breathing
        const dx = Math.sin(t * 0.38) * 6;    // ~6px horizontal sway
        const dy = Math.cos(t * 0.31) * 5;    // ~5px vertical
        const baseScale = hoverRef.current ? 1.08 : 1.02;
        const scale = baseScale + Math.sin(t * 0.24) * 0.012; // ±1.2% breath
        el.style.transform =
          `translate3d(${dx}px, ${dy}px, 0) scale(${scale})`;
      }
      rafRef.current = requestAnimationFrame(loop);
    };
    rafRef.current = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(rafRef.current);
  }, []);

  return (
    <Reveal delay={delay}>
      <div
        className="relative rounded-xl overflow-hidden group cursor-pointer h-full"
        style={{
          boxShadow: "0 15px 40px -20px rgba(15,28,58,0.2)",
          transition: "transform 400ms cubic-bezier(0.22, 1, 0.36, 1), box-shadow 400ms ease",
          transform: hover ? "translateY(-6px)" : "translateY(0)",
        }}
        onMouseEnter={() => {
          setHover(true);
          hoverRef.current = true;
        }}
        onMouseLeave={() => {
          setHover(false);
          hoverRef.current = false;
        }}
      >
        {/* Image */}
        <div className="relative aspect-[4/5] overflow-hidden">
          <img
            ref={imgRef}
            src={img}
            alt={title}
            className="w-full h-full object-cover"
            style={{
              willChange: "transform",
              transform: "translate3d(0,0,0) scale(1.02)",
              transformOrigin: "50% 50%",
              transition: "filter 500ms ease",
            }}
            loading="lazy"
            draggable={false}
          />
          {/* Gradient overlay */}
          <div
            className="absolute inset-0"
            style={{
              background:
                "linear-gradient(180deg, rgba(15,28,58,0.0) 0%, rgba(15,28,58,0.15) 50%, rgba(15,28,58,0.85) 100%)",
            }}
          />
          {/* Animated icon */}
          <div
            className="absolute top-4 left-4 w-11 h-11 rounded-full flex items-center justify-center transition-all duration-500"
            style={{
              backgroundColor: hover ? "#F28C1F" : "rgba(255,255,255,0.95)",
              transform: hover ? "scale(1.1) rotate(6deg)" : "scale(1)",
            }}
          >
            <Icon
              className="w-5 h-5 transition-colors duration-500"
              style={{ color: hover ? "#ffffff" : "#B22626" }}
              strokeWidth={2}
            />
          </div>
          {/* Stat pill (only if provided) */}
          {typeof stat === "number" && (
            <div className="absolute top-4 right-4 bg-white/90 backdrop-blur-sm px-3 py-1.5 rounded-full">
              <div className="text-[13px] font-bold" style={{ color: "#B22626" }}>
                <CountUp end={stat} suffix={statLabel} />
              </div>
            </div>
          )}
        </div>

        {/* Text content */}
        <div className="absolute bottom-0 left-0 right-0 p-5 md:p-6">
          <h4 className="text-white font-bold text-lg md:text-xl mb-1 leading-tight">
            {title}
          </h4>
          <div
            className="h-[2px] rounded-full mb-2 transition-all duration-500"
            style={{
              backgroundColor: "#F28C1F",
              width: hover ? 56 : 32,
            }}
          />
          <p
            className="text-white/90 text-[13.5px] leading-relaxed transition-all duration-500 overflow-hidden"
            style={{
              maxHeight: hover ? 200 : 0,
              opacity: hover ? 1 : 0,
              marginTop: hover ? 4 : 0,
            }}
          >
            {desc}
          </p>
        </div>
      </div>
    </Reveal>
  );
};

export const VisionMissionSection = () => {
  const pillars = [
    {
      img: PILLAR_IMAGES.design,
      icon: Compass,
      title: "Design-Led Products",
      desc: "In-house design studios turn trend research and buyer briefs into ready-to-ship collections.",
      stat: 250,
      statLabel: "+ SKUs",
    },
    {
      img: PILLAR_IMAGES.manufacturing,
      icon: Factory,
      title: "Responsible Manufacturing",
      desc: "Vertically integrated plants running on solar power with closed-loop water systems.",
      stat: 100,
      statLabel: "% owned",
    },
    {
      img: PILLAR_IMAGES.craftsmanship,
      icon: Target,
      title: "Skilled Craftsmanship",
      desc: "Master artisans and technicians backed by decades of textile heritage from Karur.",
    },
    {
      img: PILLAR_IMAGES.delivery,
      icon: Package,
      title: "Dependable Delivery",
      desc: "Predictable lead times and export execution to global buyers, on time every time.",
    },
  ];

  return (
    <section
      id="vision"
      className="relative w-full scroll-mt-20 overflow-hidden"
      style={{ backgroundColor: "#FBF6EA" }}
    >
      <div className="w-full h-[3px]" style={{ backgroundColor: "#F28C1F" }} />

      {/* Ambient background */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div
          className="absolute top-1/4 -right-32 w-[420px] h-[420px] rounded-full opacity-40 vm-blob"
          style={{
            background:
              "radial-gradient(circle, rgba(242,140,31,0.22) 0%, rgba(242,140,31,0) 70%)",
          }}
        />
        <div
          className="absolute bottom-1/4 -left-32 w-[420px] h-[420px] rounded-full opacity-40 vm-blob-2"
          style={{
            background:
              "radial-gradient(circle, rgba(178,38,38,0.15) 0%, rgba(178,38,38,0) 70%)",
          }}
        />
        {/* Decorative thread lines */}
        <svg
          className="absolute inset-0 w-full h-full opacity-10 vm-thread"
          viewBox="0 0 1200 800"
          preserveAspectRatio="none"
          aria-hidden="true"
        >
          <path
            d="M0,120 Q300,180 600,120 T1200,120"
            stroke="#B22626"
            strokeWidth="1.5"
            fill="none"
          />
          <path
            d="M0,680 Q300,620 600,680 T1200,680"
            stroke="#F28C1F"
            strokeWidth="1.5"
            fill="none"
          />
        </svg>
      </div>

      <div className="relative max-w-[1440px] mx-auto px-6 md:px-10 py-16 md:py-24">
        {/* Section header */}
        <Reveal>
          <div className="text-center max-w-3xl mx-auto mb-12 md:mb-16">
            <h2
              className="cert-title-shimmer mb-4"
              style={{
                fontFamily: "'Playfair Display', Georgia, serif",
                fontSize: "clamp(2rem, 4vw, 3rem)",
                fontWeight: 700,
                letterSpacing: "-0.015em",
              }}
            >
              Driven by Vision, Guided by Mission
            </h2>
            <p className="text-slate-700 text-[15px] md:text-base leading-relaxed">
              A shared belief in thoughtful design, honest craft and reliable
              delivery — the compass that steers every decision at Amber Living.
            </p>
          </div>
        </Reveal>

        {/* Vision + Mission split */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8 mb-16 md:mb-24">
          <StatementCard
            icon={Target}
            iconBg="#FEF2F2"
            iconColor="#B22626"
            tag="Our Vision"
            title="To be the most trusted home textile partner for global brands."
            body="Recognised for design integrity, disciplined quality control, and lasting relationships built on transparency across every yard we ship."
            delay={100}
          />
          <StatementCard
            icon={Compass}
            iconBg="#FFF6E6"
            iconColor="#F28C1F"
            tag="Our Mission"
            title="Craft thoughtfully engineered textiles through an integrated supply chain."
            body="Combining in-house design, responsible manufacturing, and dependable export execution for every partner we serve."
            delay={250}
          />
        </div>

        {/* Four pillars */}
        <Reveal>
          <div className="text-center max-w-2xl mx-auto mb-10">
            <div
              className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold tracking-widest uppercase mb-3"
              style={{ backgroundColor: "#FFF6E6", color: "#B57200" }}
            >
              <ShieldCheck className="w-3.5 h-3.5" />
              How We Deliver
            </div>
            <h3
              className="text-[#0F1C3A] mb-3"
              style={{
                fontFamily: "'Playfair Display', Georgia, serif",
                fontSize: "clamp(1.5rem, 2.6vw, 2rem)",
                fontWeight: 600,
              }}
            >
              Four pillars that shape every collection
            </h3>
            <p className="text-slate-700 text-[14.5px] leading-relaxed">
              Hover any pillar to explore how it comes to life inside our
              vertically integrated facility.
            </p>
          </div>
        </Reveal>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
          {pillars.map((p, i) => (
            <PillarCard key={p.title} {...p} delay={100 + i * 120} />
          ))}
        </div>

        {/* Bottom quote / signature — live 3D promise */}
        <Reveal delay={300}>
          <div
            className="mt-14 md:mt-20 rounded-2xl px-6 md:px-10 py-10 md:py-14 relative overflow-hidden promise-card-3d"
            style={{
              background:
                "linear-gradient(135deg, #241333 0%, #3B1930 55%, #7A3A52 100%)",
              perspective: "1200px",
            }}
          >
            {/* Ambient glow orbs */}
            <div
              className="absolute -top-16 -right-10 w-64 h-64 rounded-full infra-glow-blob"
              style={{
                background:
                  "radial-gradient(circle, rgba(244,195,205,0.35) 0%, rgba(244,195,205,0) 70%)",
              }}
            />
            <div
              className="absolute -bottom-16 -left-10 w-64 h-64 rounded-full infra-glow-blob delay-2"
              style={{
                background:
                  "radial-gradient(circle, rgba(179,100,120,0.35) 0%, rgba(179,100,120,0) 70%)",
              }}
            />
            <div
              className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[420px] h-[420px] rounded-full infra-glow-blob delay-1"
              style={{
                background:
                  "radial-gradient(circle, rgba(242,195,205,0.15) 0%, rgba(242,195,205,0) 70%)",
              }}
            />
            {/* Thread SVG */}
            <svg
              className="absolute inset-0 w-full h-full opacity-[0.08] pointer-events-none"
              viewBox="0 0 1200 300"
              preserveAspectRatio="none"
              aria-hidden="true"
            >
              <path
                d="M0,150 Q300,80 600,150 T1200,150"
                stroke="#F4C3CD"
                strokeWidth="1.4"
                fill="none"
              />
              <path
                d="M0,200 Q300,260 600,200 T1200,200"
                stroke="#B36478"
                strokeWidth="1.2"
                fill="none"
              />
            </svg>

            <div className="relative flex flex-col md:flex-row md:items-center gap-6 md:gap-10 promise-card-inner">
              <div
                className="shrink-0 w-16 h-16 rounded-full flex items-center justify-center promise-icon-orbit"
                style={{
                  background:
                    "linear-gradient(135deg, #B36478 0%, #F4C3CD 100%)",
                  boxShadow:
                    "0 15px 35px -10px rgba(179,100,120,0.6), 0 0 30px rgba(244,195,205,0.35)",
                }}
              >
                <Target
                  className="w-7 h-7 text-white"
                  strokeWidth={2.2}
                />
              </div>
              <div className="flex-1" style={{ perspective: "1000px" }}>
                <p
                  className="text-white leading-relaxed promise-quote-3d"
                  style={{
                    fontFamily: "'Playfair Display', Georgia, serif",
                    fontSize: "clamp(1.25rem, 2vw, 1.65rem)",
                    fontWeight: 500,
                    fontStyle: "italic",
                    textShadow:
                      "0 2px 0 rgba(0,0,0,0.15), 0 4px 20px rgba(179,100,120,0.35)",
                  }}
                >
                  <span className="promise-word" style={{ animationDelay: "0.1s" }}>&ldquo;Every</span>{" "}
                  <span className="promise-word" style={{ animationDelay: "0.18s" }}>thread</span>{" "}
                  <span className="promise-word" style={{ animationDelay: "0.26s" }}>we</span>{" "}
                  <span className="promise-word" style={{ animationDelay: "0.34s" }}>weave</span>{" "}
                  <span className="promise-word" style={{ animationDelay: "0.42s" }}>carries</span>{" "}
                  <span className="promise-word" style={{ animationDelay: "0.5s" }}>the</span>{" "}
                  <span className="promise-word" style={{ animationDelay: "0.58s" }}>promise</span>{" "}
                  <span className="promise-word" style={{ animationDelay: "0.66s" }}>of</span>{" "}
                  <span
                    className="promise-word promise-highlight-glow"
                    style={{ animationDelay: "0.74s" }}
                  >
                    quality,
                  </span>{" "}
                  <span
                    className="promise-word promise-highlight-glow"
                    style={{ animationDelay: "0.86s" }}
                  >
                    responsibility
                  </span>{" "}
                  <span className="promise-word" style={{ animationDelay: "0.94s" }}>and</span>{" "}
                  <span
                    className="promise-word promise-highlight-glow"
                    style={{ animationDelay: "1.02s" }}
                  >
                    trust
                  </span>{" "}
                  <span className="promise-word" style={{ animationDelay: "1.14s" }}>— the</span>{" "}
                  <span className="promise-word" style={{ animationDelay: "1.22s" }}>compass</span>{" "}
                  <span className="promise-word" style={{ animationDelay: "1.3s" }}>that has guided</span>{" "}
                  <span className="promise-word" style={{ animationDelay: "1.42s" }}>Amber Living</span>{" "}
                  <span className="promise-word" style={{ animationDelay: "1.54s" }}>from Karur</span>{" "}
                  <span className="promise-word" style={{ animationDelay: "1.66s" }}>to homes across the world.&rdquo;</span>
                </p>
                <div
                  className="mt-4 text-[12.5px] uppercase tracking-[0.32em] flex items-center gap-3 promise-signature"
                  style={{ color: "#F4C3CD" }}
                >
                  <span
                    className="inline-block h-px flex-shrink-0"
                    style={{ width: 32, backgroundColor: "#F4C3CD" }}
                  />
                  The Amber Living Promise
                </div>
              </div>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
};

// ----- Infrastructure (was Capabilities) -----
const FACTORY_AERIAL =
  "https://customer-assets-jai6qajn.emergentagent.net/job_carousel-preview-9/artifacts/w1avlz3c_image.png";
const FACTORY_DOCK =
  "https://customer-assets-jai6qajn.emergentagent.net/job_carousel-preview-9/artifacts/k2vqd67q_image.png";
const SEWING_FLOOR =
  "https://customer-assets-jai6qajn.emergentagent.net/job_carousel-preview-9/artifacts/f57grw0r_image.png";
const CUTTING_FLOOR =
  "https://customer-assets-jai6qajn.emergentagent.net/job_carousel-preview-9/artifacts/7d3iygod_image.png";
const CHECKING_PACKING =
  "https://customer-assets-jai6qajn.emergentagent.net/job_carousel-preview-9/artifacts/8bu34vko_image.png";
const SHOWROOM =
  "https://customer-assets-jai6qajn.emergentagent.net/job_carousel-preview-9/artifacts/wsrkb2wh_image.png";

// Live "sheen" wrapper for a photo — subtle ken-burns + hover reveal
const LivePhoto = ({ src, alt, caption, kenburns = "left", index = 0 }) => (
  <div
    className="group relative rounded-2xl overflow-hidden pm-float photo-glow"
    style={{
      animationDelay: `${index * 0.4}s`,
    }}
  >
    <img
      src={src}
      alt={alt}
      loading="lazy"
      draggable={false}
      className={`w-full h-full object-cover transition-transform duration-[900ms] ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-[1.06] ${
        kenburns === "left" ? "infra-kenburns-l" : "infra-kenburns-r"
      }`}
      style={{ aspectRatio: "16 / 10" }}
    />
    {/* Bottom gradient scrim */}
    <div
      className="absolute inset-0 pointer-events-none"
      style={{
        background:
          "linear-gradient(180deg, rgba(59,25,48,0) 55%, rgba(59,25,48,0.65) 100%)",
      }}
    />
    {/* Cursor glare */}
    <div
      className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
      style={{
        background:
          "linear-gradient(135deg, rgba(255,255,255,0.15) 0%, rgba(255,255,255,0) 40%)",
        mixBlendMode: "overlay",
      }}
    />
    {caption && (
      <div className="absolute inset-x-0 bottom-0 p-5 md:p-6 text-white">
        <div
          className="text-[11px] tracking-[0.32em] uppercase mb-1"
          style={{ color: "#F4C3CD" }}
        >
          {caption.eyebrow}
        </div>
        <div
          className="text-[18px] md:text-[22px] font-semibold leading-tight"
          style={{
            fontFamily: "'Playfair Display', Georgia, serif",
            textShadow: "0 2px 12px rgba(0,0,0,0.5)",
          }}
        >
          {caption.title}
        </div>
      </div>
    )}
  </div>
);

const SubsectionHeader = ({ eyebrow, title, subtitle }) => (
  <div className="mb-8 md:mb-10">
    <div
      className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold tracking-widest uppercase mb-4"
      style={{
        backgroundColor: "rgba(179,100,120,0.14)",
        color: "#7A3A52",
        border: "1px solid rgba(179,100,120,0.35)",
      }}
    >
      {eyebrow}
    </div>
    <h3
      className="mb-2 leading-[1.1]"
      style={{
        color: "#3B1930",
        fontFamily: "'Playfair Display', Georgia, serif",
        fontSize: "clamp(1.8rem, 3.4vw, 2.6rem)",
        fontWeight: 600,
        letterSpacing: "-0.02em",
      }}
    >
      {title}
    </h3>
    {subtitle && (
      <p
        className="text-[15px] md:text-[16px] max-w-2xl leading-relaxed"
        style={{ color: "#5B4149" }}
      >
        {subtitle}
      </p>
    )}
  </div>
);

export const InfrastructureSection = () => {
  const [tab, setTab] = React.useState("factory");

  const TABS = [
    { id: "factory", label: "Factory" },
    { id: "cutting", label: "Cutting" },
    { id: "sewing", label: "Sewing" },
    { id: "packing", label: "Checking & Packing" },
    { id: "showroom", label: "Showroom" },
  ];

  return (
    <section
      id="infrastructure"
      className="relative w-full py-20 md:py-28 scroll-mt-20 overflow-hidden"
      style={{ backgroundColor: "#F5EFE4" }}
      data-testid="infrastructure-section"
    >
      {/* Ambient glowing blobs */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div
          className="absolute -top-32 -right-24 w-[480px] h-[480px] rounded-full infra-glow-blob"
          style={{
            background:
              "radial-gradient(circle, rgba(179,100,120,0.22) 0%, rgba(179,100,120,0) 70%)",
          }}
        />
        <div
          className="absolute -bottom-24 -left-24 w-[520px] h-[520px] rounded-full infra-glow-blob delay-1"
          style={{
            background:
              "radial-gradient(circle, rgba(59,25,48,0.16) 0%, rgba(59,25,48,0) 70%)",
          }}
        />
        <div
          className="absolute top-1/3 left-1/2 -translate-x-1/2 w-[560px] h-[560px] rounded-full infra-glow-blob delay-2"
          style={{
            background:
              "radial-gradient(circle, rgba(244,195,205,0.18) 0%, rgba(244,195,205,0) 70%)",
          }}
        />
        <div
          className="absolute top-3/4 right-1/4 w-[360px] h-[360px] rounded-full infra-glow-blob delay-3"
          style={{
            background:
              "radial-gradient(circle, rgba(179,100,120,0.15) 0%, rgba(179,100,120,0) 70%)",
          }}
        />
      </div>

      <div className="relative max-w-[1440px] mx-auto px-6 md:px-10">
        {/* Section header */}
        <div className="text-center mb-10 md:mb-12">
          <div
            className="flex justify-center mb-5"
          >
            <div
              className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold tracking-widest uppercase infra-badge-pulse"
              style={{
                backgroundColor: "rgba(179,100,120,0.14)",
                color: "#7A3A52",
                border: "1px solid rgba(179,100,120,0.35)",
              }}
            >
              <Factory className="w-3.5 h-3.5 icon-wiggle" />
              Infrastructure
            </div>
          </div>
          <h2
            className="mb-3 leading-[1.05] infra-title-shimmer"
            style={{
              fontFamily: "'Playfair Display', Georgia, serif",
              fontSize: "clamp(2.2rem, 4.4vw, 3.4rem)",
              fontWeight: 600,
              letterSpacing: "-0.02em",
            }}
          >
            Built for scale, delivered with care.
          </h2>
          <p
            className="text-[15px] md:text-[16px] max-w-2xl mx-auto mt-4 leading-relaxed"
            style={{ color: "#5B4149" }}
          >
            A vertically integrated facility in Karur where every step — from
            yarn sourcing to finished packaging — is handled in-house.
          </p>
        </div>

        {/* Tab switcher */}
        <div
          className="flex justify-center mb-10 md:mb-12"
          role="tablist"
          aria-label="Infrastructure areas"
        >
          <div
            className="inline-flex items-center gap-1 p-1.5 rounded-full"
            style={{
              backgroundColor: "rgba(255,251,244,0.85)",
              border: "1px solid rgba(59,25,48,0.12)",
              boxShadow: "0 8px 22px -14px rgba(59,25,48,0.25)",
            }}
          >
            {TABS.map((t) => {
              const active = tab === t.id;
              return (
                <button
                  key={t.id}
                  type="button"
                  role="tab"
                  aria-selected={active}
                  onClick={() => setTab(t.id)}
                  className={`px-6 md:px-8 py-2.5 rounded-full text-[13px] md:text-[14px] font-semibold tracking-[0.06em] transition-all duration-300 ${active ? "tab-active-glow" : ""}`}
                  style={{
                    backgroundColor: active ? "#3B1930" : "transparent",
                    color: active ? "#FBE9DA" : "#3B1930",
                  }}
                  data-testid={`infra-tab-${t.id}`}
                >
                  {t.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Tab content */}
        <div key={tab} className="infra-panel-enter">
          {tab === "factory" && <FactoryPanel />}
          {tab === "cutting" && <CuttingPanel />}
          {tab === "sewing" && <SewingPanel />}
          {tab === "packing" && <PackingPanel />}
          {tab === "showroom" && <ShowroomPanel />}
        </div>
      </div>
    </section>
  );
};

// ---- Factory tab content ----
const FactoryPanel = () => (
  <div>
    <div className="mb-8 md:mb-10 text-center">
      <div
        className="text-[11px] tracking-[0.32em] uppercase mb-2"
        style={{ color: "#7A3A52" }}
      >
        Our Facility
      </div>
      <h3
        className="mb-2 leading-[1.1]"
        style={{
          color: "#3B1930",
          fontFamily: "'Playfair Display', Georgia, serif",
          fontSize: "clamp(1.8rem, 3vw, 2.4rem)",
          fontWeight: 600,
        }}
      >
        The Factory
      </h3>
      <p
        className="text-[14.5px] md:text-[15px] max-w-2xl mx-auto leading-relaxed"
        style={{ color: "#5B4149" }}
      >
        Purpose-built in the Karur Textile Park — production halls, dyeing,
        cutting, quality labs, warehousing and shipping docks under one roof.
      </p>
    </div>

    <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
      <div className="lg:col-span-3">
        <LivePhoto
          src={FACTORY_AERIAL}
          alt="Amber Living factory — aerial view from Karur Textile Park"
          kenburns="left"
          index={0}
        />
      </div>
      <div className="lg:col-span-2">
        <LivePhoto
          src={FACTORY_DOCK}
          alt="Amber Living factory — loading dock and shipping bay"
          kenburns="right"
          index={1}
        />
      </div>
    </div>
  </div>
);

// ---- Showroom tab ----
const ShowroomPanel = () => (
  <div>
    <div className="mb-8 md:mb-10 text-center">
      <div
        className="text-[11px] tracking-[0.32em] uppercase mb-2"
        style={{ color: "#7A3A52" }}
      >
        Experience Space
      </div>
      <h3
        className="mb-2 leading-[1.1]"
        style={{
          color: "#3B1930",
          fontFamily: "'Playfair Display', Georgia, serif",
          fontSize: "clamp(1.8rem, 3vw, 2.4rem)",
          fontWeight: 600,
        }}
      >
        Our Showroom
      </h3>
      <p
        className="text-[14.5px] md:text-[15px] max-w-2xl mx-auto leading-relaxed"
        style={{ color: "#5B4149" }}
      >
        A curated space at our Karur campus where buyers can see, feel and
        source across the full Amber Living range — from table linens to
        cushions, throws and curtains.
      </p>
    </div>

    <LivePhoto
      src={SHOWROOM}
      alt="Amber Living showroom — curated product display at Karur campus"
      kenburns="left"
      index={0}
    />
  </div>
);

// ---- Checking & Packing tab ----
const PackingPanel = () => (
  <div>
    <div className="mb-8 md:mb-10 text-center">
      <div
        className="text-[11px] tracking-[0.32em] uppercase mb-2"
        style={{ color: "#7A3A52" }}
      >
        Quality & Dispatch
      </div>
      <h3
        className="mb-2 leading-[1.1]"
        style={{
          color: "#3B1930",
          fontFamily: "'Playfair Display', Georgia, serif",
          fontSize: "clamp(1.8rem, 3vw, 2.4rem)",
          fontWeight: 600,
        }}
      >
        Checking &amp; Packing
      </h3>
      <p
        className="text-[14.5px] md:text-[15px] max-w-2xl mx-auto leading-relaxed"
        style={{ color: "#5B4149" }}
      >
        Every piece is inspected, folded, and packed to buyer specification —
        ensuring what leaves Karur arrives shelf-ready anywhere in the world.
      </p>
    </div>

    <LivePhoto
      src={CHECKING_PACKING}
      alt="Amber Living checking and packing floor — QC and dispatch prep"
      kenburns="right"
      index={0}
    />
  </div>
);

// ---- Cutting tab content ----
const CuttingPanel = () => (
  <div>
    <div className="mb-8 md:mb-10 text-center">
      <div
        className="text-[11px] tracking-[0.32em] uppercase mb-2"
        style={{ color: "#7A3A52" }}
      >
        Precision Automation
      </div>
      <h3
        className="mb-2 leading-[1.1]"
        style={{
          color: "#3B1930",
          fontFamily: "'Playfair Display', Georgia, serif",
          fontSize: "clamp(1.8rem, 3vw, 2.4rem)",
          fontWeight: 600,
        }}
      >
        The Cutting Floor
      </h3>
      <p
        className="text-[14.5px] md:text-[15px] max-w-2xl mx-auto leading-relaxed"
        style={{ color: "#5B4149" }}
      >
        Computer-guided fabric cutting with automated laying and marker
        optimisation — accurate to the millimetre, consistent across every
        production run.
      </p>
    </div>

    <LivePhoto
      src={CUTTING_FLOOR}
      alt="Amber Living cutting floor — automated fabric cutting machine in operation"
      kenburns="left"
      index={0}
    />
  </div>
);

// ---- Sewing tab content ----
const SewingPanel = () => (
  <div>
    <div className="mb-8 md:mb-10 text-center">
      <div
        className="text-[11px] tracking-[0.32em] uppercase mb-2"
        style={{ color: "#7A3A52" }}
      >
        Craftsmanship
      </div>
      <h3
        className="mb-2 leading-[1.1]"
        style={{
          color: "#3B1930",
          fontFamily: "'Playfair Display', Georgia, serif",
          fontSize: "clamp(1.8rem, 3vw, 2.4rem)",
          fontWeight: 600,
        }}
      >
        The Sewing Floor
      </h3>
      <p
        className="text-[14.5px] md:text-[15px] max-w-2xl mx-auto leading-relaxed"
        style={{ color: "#5B4149" }}
      >
        Skilled operators, precision machines and rigorous quality checks —
        where every seam meets our export standard.
      </p>
    </div>

    <LivePhoto
      src={SEWING_FLOOR}
      alt="Amber Living sewing floor — production lines in operation"
      kenburns="left"
      index={0}
    />
  </div>
);

// ----- Product Mix (was Products) -----
export const ProductMixSection = () => {
  const categories = [
    {
      title: "Kitchen & Dining",
      products: [
        "Tea Towels",
        "Placemats",
        "Table Runners",
        "Tablecloths",
        "Gloves & Aprons",
      ],
      img: "https://images.unsplash.com/photo-1544148103-0773bf10d330?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
      objectPosition: "center",
      fallback:
        "https://images.unsplash.com/photo-1414235077428-338989a2e8c0?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
    },
    {
      title: "Living & Decor",
      products: [
        "Cushions",
        "Throws",
        "Chair Pads",
        "Floor Cushions",
        "Curtains",
      ],
      img: "https://images.unsplash.com/photo-1616486338812-3dadae4b4ace?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
      objectPosition: "center",
      fallback:
        "https://images.pexels.com/photos/1571459/pexels-photo-1571459.jpeg?auto=compress&cs=tinysrgb&w=1200",
    },
    {
      title: "Kids & Nursery",
      products: [
        "Kids Textiles",
        "Baby Accessories",
        "Soft Home Essentials",
      ],
      img: "https://images.unsplash.com/photo-1519689680058-324335c77eba?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
      objectPosition: "center",
      fallback:
        "https://images.pexels.com/photos/1648776/pexels-photo-1648776.jpeg?auto=compress&cs=tinysrgb&w=1200",
    },
    {
      title: "Outdoor & Lifestyle",
      products: [
        "Beach Towels",
        "Outdoor Cushions",
        "Sleeping Bags",
        "Lifestyle Accessories",
      ],
      img: "https://images.unsplash.com/photo-1519046904884-53103b34b206?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
      objectPosition: "center",
      fallback:
        "https://images.pexels.com/photos/1024960/pexels-photo-1024960.jpeg?auto=compress&cs=tinysrgb&w=1200",
    },
  ];

  return (
    <section
      id="product-mix"
      className="relative w-full py-20 md:py-28 scroll-mt-20 overflow-hidden"
      style={{ backgroundColor: "#F5EFE4" }}
      data-testid="product-mix-section"
    >
      {/* Ambient blobs */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div
          className="absolute -top-32 -left-24 w-[480px] h-[480px] rounded-full opacity-60 vm-blob"
          style={{
            background:
              "radial-gradient(circle, rgba(179,100,120,0.18) 0%, rgba(179,100,120,0) 70%)",
          }}
        />
        <div
          className="absolute -bottom-24 -right-24 w-[520px] h-[520px] rounded-full opacity-50 vm-blob-2"
          style={{
            background:
              "radial-gradient(circle, rgba(59,25,48,0.12) 0%, rgba(59,25,48,0) 70%)",
          }}
        />
      </div>

      <div className="relative max-w-[1440px] mx-auto px-6 md:px-10">
        {/* Header */}
        <div className="text-center mb-14 md:mb-16">
          <div
            className="pm-red-glow inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold tracking-widest uppercase mb-5"
            style={{
              backgroundColor: "rgba(179,100,120,0.14)",
              color: "#7A3A52",
              border: "1px solid rgba(220,60,80,0.55)",
            }}
          >
            <Package className="w-3.5 h-3.5 icon-wiggle" />
            Product Mix
          </div>
          <h2
            className="mb-3 leading-[1.05]"
            style={{
              color: "#3B1930",
              fontFamily: "'Playfair Display', Georgia, serif",
              fontSize: "clamp(2.2rem, 4.4vw, 3.4rem)",
              fontWeight: 600,
              letterSpacing: "-0.02em",
            }}
          >
            Textiles for every corner
            <br />
            of the home.
          </h2>
          <p
            className="text-[15px] md:text-[16px] max-w-2xl mx-auto mt-4 leading-relaxed"
            style={{ color: "#5B4149" }}
          >
            Four collections crafted at our Karur facility — design-led,
            responsibly made, and shipped to the world&apos;s most-loved home
            brands.
          </p>
        </div>

        {/* Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
          {categories.map((c, i) => (
            <ProductTile key={c.title} c={c} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
};

// Product category tile with continuous float + always-visible product list
const ProductTile = ({ c, index }) => {
  const [src, setSrc] = React.useState(c.img);
  return (
    <a
      href="#contact"
      className="group relative block rounded-2xl overflow-hidden pm-float"
      style={{
        aspectRatio: "4 / 5.4",
        boxShadow:
          "0 20px 45px -25px rgba(59,25,48,0.35), 0 8px 20px -12px rgba(59,25,48,0.18)",
        animationDelay: `${index * 0.35}s`,
      }}
      data-testid={`product-tile-${c.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
    >
      {/* Image */}
      <img
        src={src}
        onError={() => setSrc(c.fallback)}
        alt={c.title}
        className="absolute inset-0 w-full h-full object-cover transition-transform duration-[900ms] ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-[1.08]"
        loading="lazy"
        draggable={false}
        style={{ objectPosition: c.objectPosition || "center" }}
      />
      {/* Gradient overlay – stronger so product list stays legible */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "linear-gradient(180deg, rgba(30,10,20,0) 25%, rgba(30,10,20,0.55) 62%, rgba(30,10,20,0.9) 100%)",
        }}
      />
      {/* Top vignette */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "linear-gradient(180deg, rgba(59,25,48,0.15) 0%, rgba(59,25,48,0) 25%)",
        }}
      />
      {/* Text block */}
      <div className="absolute inset-x-0 bottom-0 p-5 md:p-6 text-white">
        <div
          className="text-[19px] md:text-[22px] font-semibold leading-tight mb-3"
          style={{
            fontFamily: "'Playfair Display', Georgia, serif",
            textShadow: "0 2px 12px rgba(0,0,0,0.5)",
          }}
        >
          {c.title}
        </div>
        <ul className="space-y-1">
          {c.products.map((p) => (
            <li
              key={p}
              className="flex items-start gap-2 text-[12.5px] md:text-[13px] leading-snug"
              style={{
                color: "rgba(255,255,255,0.92)",
                textShadow: "0 1px 6px rgba(0,0,0,0.5)",
              }}
            >
              <span
                className="mt-1.5 inline-block w-1 h-1 rounded-full shrink-0"
                style={{ backgroundColor: "#F4C3CD" }}
              />
              {p}
            </li>
          ))}
        </ul>
      </div>

      {/* Corner arrow badge */}
      <div
        className="absolute top-4 right-4 w-9 h-9 rounded-full flex items-center justify-center transition-all duration-300 group-hover:-translate-y-0.5 group-hover:rotate-45"
        style={{
          backgroundColor: "rgba(255,255,255,0.95)",
          color: "#3B1930",
          boxShadow: "0 6px 16px -6px rgba(59,25,48,0.35)",
        }}
      >
        <ChevronRight className="w-4 h-4" strokeWidth={2.2} />
      </div>
    </a>
  );
};

// ----- Certifications -----
// Real official logos + a custom GRS SVG that matches the reference
const OEKO_LOGO =
  "https://upload.wikimedia.org/wikipedia/commons/d/d0/Oeko-tex_standard100_logo_rgb-2022.jpg";
const GOTS_LOGO = "/gots-logo-clean.png";
const BETTER_COTTON_LOGO = "/bci-logo-clean.png";
const GRS_LOGO = "/grs-logo-clean.png";

// A small decorative QR-like SVG (visual only, matches reference)
const QrDecor = () => (
  <svg viewBox="0 0 60 60" className="w-full h-full">
    <rect x="0" y="0" width="60" height="60" fill="white" stroke="#1E1338" strokeWidth="1.5" />
    {/* Corner markers */}
    {[
      [4, 4], [42, 4], [4, 42],
    ].map(([x, y], i) => (
      <g key={i}>
        <rect x={x} y={y} width="14" height="14" fill="#1E1338" />
        <rect x={x + 2} y={y + 2} width="10" height="10" fill="white" />
        <rect x={x + 4} y={y + 4} width="6" height="6" fill="#1E1338" />
      </g>
    ))}
    {/* Data pattern */}
    {[
      [22, 6, 3, 3], [28, 6, 3, 3], [34, 6, 3, 3],
      [22, 12, 3, 3], [30, 12, 3, 3],
      [24, 18, 3, 3], [32, 18, 3, 3], [40, 22, 3, 3],
      [22, 24, 3, 3], [28, 24, 3, 3], [46, 26, 3, 3],
      [24, 30, 3, 3], [34, 30, 3, 3], [42, 32, 3, 3],
      [22, 36, 3, 3], [30, 36, 3, 3], [50, 38, 3, 3],
      [24, 42, 3, 3], [40, 42, 3, 3], [46, 44, 3, 3],
      [30, 46, 3, 3], [36, 46, 3, 3], [52, 48, 3, 3],
      [22, 50, 3, 3], [26, 52, 3, 3], [40, 50, 3, 3],
    ].map((coords, i) => (
      <rect key={i} x={coords[0]} y={coords[1]} width={coords[2]} height={coords[3]} fill="#1E1338" />
    ))}
  </svg>
);

// Custom GRS logo SVG (navy globe + circular arrow)
const GrsLogo = () => (
  <svg viewBox="0 0 140 100" className="w-full h-full">
    <g transform="translate(70, 40)" fill="none" stroke="#0B3E7A" strokeLinecap="round" strokeLinejoin="round">
      {/* Circular arrow */}
      <path d="M -26 -8 A 26 26 0 1 1 -26 8" strokeWidth="4" />
      <polyline points="-28,-13 -26,-8 -19,-10" strokeWidth="4" />
      <polyline points="24,3 26,8 19,6" strokeWidth="4" />
      {/* Globe */}
      <circle cx="0" cy="0" r="13" strokeWidth="1.8" />
      <ellipse cx="0" cy="0" rx="5" ry="13" strokeWidth="1.4" />
      <line x1="-13" y1="0" x2="13" y2="0" strokeWidth="1.4" />
      <path d="M -12 -6 L 12 -6" strokeWidth="1.2" />
      <path d="M -12 6 L 12 6" strokeWidth="1.2" />
    </g>
    <text x="70" y="82" textAnchor="middle" fill="#0B3E7A" fontFamily="Arial, sans-serif" fontWeight="700" fontSize="10">GLOBAL RECYCLED</text>
    <text x="70" y="94" textAnchor="middle" fill="#0B3E7A" fontFamily="Arial, sans-serif" fontWeight="700" fontSize="10">STANDARD</text>
  </svg>
);

// Custom Better Cotton logo SVG (green leaf 'b' + wordmark) matching reference
const BetterCottonLogo = () => (
  <svg viewBox="0 0 200 100" className="w-full h-full">
    {/* Leaf b */}
    <g transform="translate(40, 32)">
      <circle cx="12" cy="12" r="22" fill="#5AB748" />
      <path d="M 5 22 L 5 -4 Q 5 -6 7 -6 Q 9 -6 9 -4 L 9 15 Q 9 22 15 22 Q 22 22 22 15 Q 22 8 15 8 Q 12 8 10 10" stroke="white" strokeWidth="3" fill="none" strokeLinecap="round" />
    </g>
    <text x="80" y="42" fill="#333" fontFamily="Arial, sans-serif" fontWeight="600" fontSize="17">better</text>
    <text x="80" y="62" fill="#5AB748" fontFamily="Arial, sans-serif" fontWeight="700" fontSize="17">cotton</text>
    <text x="152" y="46" fill="#333" fontFamily="Arial, sans-serif" fontWeight="600" fontSize="9">™</text>
  </svg>
);

// Interactive 3D flip + tilt card for each certification
const CertCard = ({ cert, index }) => {
  const cardRef = React.useRef(null);
  const [tilt, setTilt] = React.useState({ x: 0, y: 0, gx: 50, gy: 50, hover: false });
  const [flipped, setFlipped] = React.useState(false);

  const onMove = (e) => {
    const el = cardRef.current;
    if (!el || flipped) return;
    const rect = el.getBoundingClientRect();
    const px = (e.clientX - rect.left) / rect.width;
    const py = (e.clientY - rect.top) / rect.height;
    setTilt({
      x: (0.5 - py) * 14,
      y: (px - 0.5) * 14,
      gx: px * 100,
      gy: py * 100,
      hover: true,
    });
  };

  const onLeave = () =>
    setTilt({ x: 0, y: 0, gx: 50, gy: 50, hover: false });

  return (
    <div
      className="relative"
      style={{ perspective: "1200px" }}
      onMouseMove={onMove}
      onMouseLeave={onLeave}
    >
      {/* Outer wrapper handles the idle float animation */}
      <div
        className="cert-float"
        style={{
          animationDelay: `${index * 0.3}s`,
          minHeight: "360px",
        }}
      >
        {/* Inner card handles flip + tilt transforms */}
        <div
          ref={cardRef}
          className="relative w-full h-full"
          style={{
            transformStyle: "preserve-3d",
            transform: `rotateY(${flipped ? 180 : tilt.y}deg) rotateX(${flipped ? 0 : tilt.x}deg) translateZ(${tilt.hover && !flipped ? 20 : 0}px)`,
            transition: tilt.hover && !flipped
              ? "transform 80ms linear"
              : "transform 700ms cubic-bezier(0.22, 1, 0.36, 1)",
            minHeight: "360px",
          }}
        >
        {/* FRONT */}
        <div
          className="absolute inset-0 rounded-xl bg-white border p-5 md:p-6 flex flex-col"
          style={{
            borderColor: "#F2D9B8",
            backfaceVisibility: "hidden",
            boxShadow: tilt.hover
              ? "0 30px 60px -20px rgba(15,28,58,0.35), 0 15px 30px -12px rgba(242,140,31,0.25)"
              : "0 8px 22px -12px rgba(15,28,58,0.15)",
            transition: "box-shadow 300ms ease",
          }}
        >
          {/* Logo area */}
          <div
            className={`flex items-center justify-center gap-3 mb-4 ${cert.titleInImage ? "h-52 md:h-56" : "h-28 md:h-32"}`}
            style={{ transform: "translateZ(40px)", transformStyle: "preserve-3d" }}
          >
            {cert.logoLeft && (
              <div className="h-full flex items-center">{cert.logoLeft}</div>
            )}
            {cert.logoImg && (
              <img
                src={cert.logoImg}
                alt={cert.logoAlt}
                className="max-h-full max-w-full object-contain"
                loading="lazy"
                draggable={false}
              />
            )}
            {cert.logoSvg && (
              <div className="h-full w-full flex items-center justify-center">
                {cert.logoSvg}
              </div>
            )}
          </div>

          {!cert.titleInImage && (
            <div className="text-center mb-3" style={{ transform: "translateZ(28px)" }}>
              {cert.title.map((line, i) => (
                <div
                  key={i}
                  className="text-[#0F1C3A] font-bold text-[13px] md:text-[14px] leading-snug tracking-wide"
                >
                  {line}
                </div>
              ))}
            </div>
          )}

          {!cert.titleInImage && (
            <div
              className="mx-auto mb-3 h-[2px] w-10 rounded-full transition-all duration-300"
              style={{
                backgroundColor: "#F28C1F",
                width: tilt.hover ? 64 : 40,
                transform: "translateZ(22px)",
              }}
            />
          )}

          <p
            className="text-center text-slate-600 text-[13px] leading-relaxed flex-1"
            style={{ transform: "translateZ(16px)" }}
          >
            {cert.desc}
          </p>

          <button
            type="button"
            onClick={() => setFlipped(true)}
            className="mt-3 mx-auto inline-flex items-center gap-1 text-[12px] font-semibold uppercase tracking-widest transition-colors"
            style={{ color: "#B22626", transform: "translateZ(20px)" }}
            onMouseEnter={(e) => (e.currentTarget.style.color = "#8C1F1F")}
            onMouseLeave={(e) => (e.currentTarget.style.color = "#B22626")}
          >
            Learn More <ChevronRight className="w-3.5 h-3.5" />
          </button>

          {/* Quick download button on front (only if PDF available) */}
          {cert.pdfUrl && (
            <a
              href={cert.pdfUrl}
              download={cert.pdfName}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="absolute top-3 right-3 inline-flex items-center justify-center w-8 h-8 rounded-full transition-all duration-200 hover:scale-110"
              style={{
                backgroundColor: "#FEF2F2",
                color: "#B22626",
                transform: "translateZ(30px)",
              }}
              title="Download certificate PDF"
              aria-label={`Download ${cert.logoAlt} certificate`}
            >
              <Download className="w-4 h-4" strokeWidth={2.2} />
            </a>
          )}

          {/* Cursor glare */}
          <div
            className="pointer-events-none absolute inset-0 rounded-xl transition-opacity duration-300"
            style={{
              opacity: tilt.hover ? 1 : 0,
              background: `radial-gradient(circle at ${tilt.gx}% ${tilt.gy}%, rgba(255,255,255,0.55) 0%, rgba(255,255,255,0) 45%)`,
              mixBlendMode: "overlay",
            }}
          />
          {/* Corner shimmer */}
          <div
            className="pointer-events-none absolute inset-0 rounded-xl transition-opacity duration-500"
            style={{
              opacity: tilt.hover ? 1 : 0,
              background:
                "linear-gradient(135deg, rgba(242,140,31,0.15) 0%, rgba(242,140,31,0) 40%, rgba(242,140,31,0) 60%, rgba(242,140,31,0.15) 100%)",
            }}
          />
        </div>

        {/* BACK */}
        <div
          className="absolute inset-0 rounded-xl p-5 md:p-6 flex flex-col justify-between"
          style={{
            backgroundColor: "#241333",
            backfaceVisibility: "hidden",
            transform: "rotateY(180deg)",
            boxShadow: "0 30px 60px -20px rgba(15,28,58,0.4)",
          }}
        >
          <div>
            <div
              className="w-11 h-11 rounded-full flex items-center justify-center mb-4"
              style={{ backgroundColor: "#F28C1F" }}
            >
              <ShieldCheck className="w-5 h-5 text-white" strokeWidth={2.2} />
            </div>
            <div className="text-white font-bold text-[15px] md:text-[16px] mb-3 leading-tight">
              {cert.title.join(" ")}
            </div>
            <p className="text-white/85 text-[13px] leading-relaxed mb-3">
              {cert.detail}
            </p>
            <ul className="space-y-1.5 mb-3">
              {cert.points.map((pt, i) => (
                <li
                  key={i}
                  className="text-white/80 text-[12.5px] flex items-start gap-1.5"
                >
                  <span
                    className="mt-1.5 w-1.5 h-1.5 rounded-full shrink-0"
                    style={{ backgroundColor: "#F28C1F" }}
                  />
                  {pt}
                </li>
              ))}
            </ul>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <button
              type="button"
              onClick={() => setFlipped(false)}
              className="inline-flex items-center gap-1 text-[12px] font-semibold uppercase tracking-widest text-white/90 hover:text-white transition-colors"
            >
              <ChevronLeft className="w-3.5 h-3.5" /> Back
            </button>
            {cert.pdfUrl ? (
              <a
                href={cert.pdfUrl}
                download={cert.pdfName}
                target="_blank"
                rel="noopener noreferrer"
                className="ml-auto inline-flex items-center gap-1.5 px-3 py-2 rounded-md text-[11.5px] font-semibold uppercase tracking-widest transition-all duration-200 hover:-translate-y-0.5"
                style={{ backgroundColor: "#F28C1F", color: "#ffffff" }}
                onMouseEnter={(e) =>
                  (e.currentTarget.style.backgroundColor = "#E07A0F")
                }
                onMouseLeave={(e) =>
                  (e.currentTarget.style.backgroundColor = "#F28C1F")
                }
              >
                <Download className="w-3.5 h-3.5" />
                Download PDF
              </a>
            ) : (
              <span className="ml-auto inline-flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-widest text-white/60 italic">
                Available on request
              </span>
            )}
          </div>
        </div>
      </div>
      </div>
    </div>
  );
};

export const CertificationsSection = () => {
  const certs = [
    {
      logoImg: OEKO_LOGO,
      logoAlt: "OEKO-TEX Standard 100",
      title: ["OEKO-TEX®", "STANDARD 100"],
      desc: "Tests every component of our products for harmful substances. Safe for you and the environment.",
      detail:
        "The world's leading health-related label for textiles. Every article certified has been tested at every stage of production.",
      points: [
        "Every thread, button and zip tested",
        "Verified by independent labs worldwide",
        "Traceable via unique QR certificate",
      ],
      pdfUrl:
        "https://customer-assets-jai6qajn.emergentagent.net/job_carousel-preview-9/artifacts/ebsncs8o_26.HIN.40552.pdf",
      pdfName: "OEKO-TEX-Standard-100-26.HIN.40552.pdf",
    },
    {
      logoImg: GOTS_LOGO,
      logoAlt: "GOTS - Global Organic Textile Standard",
      title: ["GOTS ORGANIC", "GLOBAL ORGANIC", "TEXTILE STANDARD"],
      titleInImage: true,
      desc: "Ensures textiles are organically produced from raw material to final product.",
      detail:
        "GOTS is the recognised leading standard for organic fibres, covering ecological and social criteria across the entire supply chain.",
      points: [
        "Minimum 70% certified organic fibres",
        "Bans harmful dyes & auxiliaries",
        "Strict social & labour criteria",
      ],
      pdfUrl: null,
    },
    {
      logoImg: GRS_LOGO,
      logoAlt: "Global Recycled Standard",
      title: ["GLOBAL RECYCLED", "STANDARD"],
      titleInImage: true,
      desc: "Ensures the use of recycled materials and responsible manufacturing throughout the supply chain.",
      detail:
        "A voluntary product standard tracking recycled input material from source to final product, with strict environmental and social criteria.",
      points: [
        "Verified recycled content ≥ 20%",
        "Chain-of-custody documented",
        "Chemical & social requirements audited",
      ],
      pdfUrl: null,
    },
    {
      logoImg: BETTER_COTTON_LOGO,
      logoAlt: "Better Cotton",
      title: ["BETTER COTTON™"],
      titleInImage: true,
      desc: "Supports more sustainable cotton farming for a better future for people and the planet.",
      detail:
        "Better Cotton trains farmers to grow cotton in ways that care for the environment and improve livelihoods of farming communities.",
      points: [
        "Reduced water & pesticide use",
        "Improved farmer livelihoods",
        "Sourced via mass-balance system",
      ],
      pdfUrl: null,
    },
  ];

  return (
    <section
      id="certifications"
      className="w-full scroll-mt-20 relative overflow-hidden"
      style={{ backgroundColor: "#FBF6EA" }}
    >
      {/* Thin orange top accent */}
      <div className="w-full h-[3px]" style={{ backgroundColor: "#F28C1F" }} />

      {/* Ambient decorative glows */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div
          className="absolute -top-24 -right-24 w-96 h-96 rounded-full opacity-40"
          style={{
            background:
              "radial-gradient(circle, rgba(242,140,31,0.18) 0%, rgba(242,140,31,0) 70%)",
          }}
        />
        <div
          className="absolute -bottom-24 -left-24 w-96 h-96 rounded-full opacity-40"
          style={{
            background:
              "radial-gradient(circle, rgba(36,19,51,0.12) 0%, rgba(36,19,51,0) 70%)",
          }}
        />
      </div>

      <div className="relative max-w-[1440px] mx-auto px-6 md:px-10 py-14 md:py-20">
        <div className="mb-8">
          <h2
            className="mb-3 cert-title-shimmer"
            style={{
              fontFamily: "'Playfair Display', 'Inter', Georgia, serif",
              fontSize: "clamp(2rem, 3.6vw, 2.6rem)",
              fontWeight: 700,
              letterSpacing: "-0.01em",
            }}
          >
            Certifications &amp; Standards
          </h2>
          <p className="text-slate-700 max-w-2xl text-[14.5px] md:text-[15px] leading-relaxed">
            Our certifications reflect our commitment to responsible materials,
            product safety, transparency and internationally recognised textile
            standards.
          </p>
        </div>

        <div className="mb-8">
          <h3 className="text-[#0F1C3A] font-bold mb-2 text-lg md:text-xl">
            Amber Living Certifications
          </h3>
          <p className="text-slate-700 max-w-2xl text-[14px] md:text-[14.5px] leading-relaxed">
            Hover any card for a 3D tilt, or tap “Learn More” to flip it and
            see the full details of each certification.
          </p>
        </div>

        {/* Interactive 3D cards grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
          {certs.map((c, idx) => (
            <CertCard key={idx} cert={c} index={idx} />
          ))}
        </div>

        {/* Bottom banner */}
        <div
          className="mt-10 md:mt-12 rounded-lg px-5 md:px-7 py-4 md:py-5 flex items-center gap-4"
          style={{ backgroundColor: "#241333" }}
        >
          <div
            className="shrink-0 w-9 h-9 rounded-full flex items-center justify-center cert-icon-pulse"
            style={{ backgroundColor: "#F28C1F" }}
          >
            <ShieldCheck className="w-5 h-5 text-white" strokeWidth={2.2} />
          </div>
          <p className="text-white/95 text-[13.5px] md:text-[14.5px] leading-relaxed">
            Supported by the established compliance and manufacturing systems
            of our parent company.
          </p>
        </div>
      </div>
    </section>
  );
};

// ----- Contact section extracted to /app/frontend/src/components/ContactSection.jsx -----
